import React, { useState, useRef } from "react";
import {
  TextField,
  Grid,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TableContainer,
  Pagination,
  Stack,
  Paper,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip
} from "@mui/material";
import ScaleLoader from "react-spinners/ScaleLoader";
import Visibility from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Menu from "@mui/material/Menu";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import "./CandidateRedesign.css";
import useStyles from "../../assets/styles/SupMangComp.style";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import noTableData from "../../assets/images/noTableData.svg";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useDebounce from "../../utils/useDebounce";
import CustomModel from "../../utils/CustomModel";
import { Notification } from "../../utils/Notification";
import { format } from "date-fns";
import { useGetCandidates, useDeleteCandidate } from "../../hooks/useCandidateManagement";
import { ResumeScoreChip, ResumeScoreFilters, isScorePending, isNonResumeProfile } from "../common/ResumeScore";
import useAuth from "../../hooks/useAuth";
import API from "../../utils/Axios.utils";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AdminStatusUpdateForm from "../ListOfOpenJobs/AdminStatusUpdateForm";
import DuplicateResolutionTab from "./DuplicateResolutionTab";
import { parseStatusLabel } from "../../utils/statusUtils";
import { useDuplicatePairs } from "../../hooks/useDuplicateResolution";

const CandidateMangComp = () => {
  useDuplicatePairs(); // Pre-load duplicate resolution data in background for instant tab transition
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'candidates'); // 'candidates' | 'duplicates'

  const [searchText, setSearchText] = React.useState(searchParams.get("search") || "");
  const [filterName, setFilterName] = React.useState(searchParams.get("name") || "");
  const [filterEmail, setFilterEmail] = React.useState(searchParams.get("email") || "");
  const [profileStatus, setProfileStatus] = React.useState(searchParams.get("status") || "");
  const [jobCode, setJobCode] = React.useState(searchParams.get("jobCode") || location.state?.jobCode || "");
  const [jobTitle, setJobTitle] = React.useState(searchParams.get("jobTitle") || location.state?.jobTitle || "");

  // Snackbar
  const [alert, setAlert] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState("");
  const [alertType, setAlertType] = React.useState("");

  // Pagination
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);

  // Resume score filter + sort. Applied server-side, so they act on every candidate,
  // not just the page on screen.
  const [scoreFilters, setScoreFilters] = useState({
    selectionStatus: searchParams.get("selectionStatus") || "",
    minScore: searchParams.get("minScore") || "",
    sortOrder: searchParams.get("sortOrder") || "",
  });

  const handleScoreFilterChange = (change) => {
    setScoreFilters((prev) => ({ ...prev, ...change }));
    setPage(1);   // a narrower result set invalidates the current page number
  };

  // Sync state to URL
  React.useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab && activeTab !== 'candidates') params.set("tab", activeTab);
    if (page > 1) params.set("page", page);
    if (searchText) params.set("search", searchText);
    if (filterName) params.set("name", filterName);
    if (filterEmail) params.set("email", filterEmail);
    if (profileStatus) params.set("status", profileStatus);
    if (jobCode) params.set("jobCode", jobCode);
    if (jobTitle) params.set("jobTitle", jobTitle);
    if (scoreFilters.selectionStatus) params.set("selectionStatus", scoreFilters.selectionStatus);
    if (scoreFilters.minScore) params.set("minScore", scoreFilters.minScore);
    if (scoreFilters.sortOrder) params.set("sortOrder", scoreFilters.sortOrder);

    // Only update if params actually changed to avoid infinite loops
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, page, searchText, filterName, filterEmail, profileStatus, jobCode, jobTitle, scoreFilters, setSearchParams, searchParams]);

  const handleAlert = () => {
    setAlert(false);
  };

  const handlePageChange = (e, value) => {
    setPage(value);
  };

  const handleNavigate = () => {
    navigate("/candidate_data/addCandidate", { state: { from: location.search } });
  };



  const { userId: userID, role, user, token } = useAuth();
  const isAdmin = role?.toLowerCase() === "admin";
  const effectiveSupplierId = isAdmin ? "all" : userID;

  const activeSearch = filterName || filterEmail || searchText;
  const debouncedCandidateQuery = useDebounce(activeSearch, 500);
  const debouncedJobCodeQuery = useDebounce(jobCode, 500);

  // React Query Hook
  const {
    data,
    isPending: showLoader,
    error
  } = useGetCandidates({
    page,
    search: debouncedCandidateQuery,
    status: profileStatus,
    jobCode: debouncedJobCodeQuery,
    supplierId: effectiveSupplierId,
    ...scoreFilters
  });

  const candidateData = data?.profile || [];
  
  const listCount = data?.totalCount || 0;

  // Handle errors from React Query
  React.useEffect(() => {
    if (error) {
      setAlertContent(error?.response?.data?.message || "An error occurred fetching candidates");
      setAlertType("error");
      setAlert(true);
    }
  }, [error]);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Reviewed', label: 'Reviewed' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'Short_listed', label: 'Short Listed' },
    { value: 'Interview_Scheduled', label: 'Interview Scheduled' },
    { value: 'Interview_Cancelled', label: 'Interview Cancelled' },
    { value: 'Staffed', label: 'Staffed' },
    { value: 'Position_on_hold', label: 'Position on Hold' },
    { value: 'Close-No_Client_Response', label: 'Close-No Client Response' }
  ];

  const getStatusColorClass = (status) => {
    return status || "Vendor_Submission";
  };

  const handleViewResume = async (details) => {
    if (!details) return;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const payload = {
      supplierId: user?.supplierId || details.supplier_id,
      coreSkillId: details.coreSkillId,
      job_code: details.job_code,
      email: details.email,
    };
    try {
      const res = await API.post(`/api/profile/candidate_document`, payload, config);
      if (res?.data?.url) {
        navigate("/candidate_data/viewFile", {
          state: { fileUrl: res.data.url, from: "/candidate_data" },
        });
      } else {
        setAlertContent("Resume URL not found");
        setAlertType("error");
        setAlert(true);
      }
    } catch (error) {
      console.error("Error fetching PDF URL:", error);
      setAlertContent("Error fetching resume");
      setAlertType("error");
      setAlert(true);
    }
  };

  if (isAdmin) {
    return (
      <div className="candidates-directory-container">
        {/* Page Header */}
        <div className="directory-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1>Candidates Directory</h1>
            <p>Manage candidates and track their individual job applications.</p>
          </div>
          <Button
            variant="contained"
            aria-haspopup="true"
            onClick={handleNavigate}
            style={{ background: "#2777AA", width: "214px", height: '40px', textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
          >
            + Add Candidate
          </Button>
        </div>
        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
          <button
            id="tab-all-candidates"
            onClick={() => setActiveTab('candidates')}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'candidates' ? 700 : 400,
              borderBottom: activeTab === 'candidates' ? '3px solid #2777AA' : '3px solid transparent',
              color: activeTab === 'candidates' ? '#2777AA' : '#616161',
              fontSize: '14px',
              transition: 'all 0.2s',
            }}
          >
            All Candidates
          </button>
          <button
            id="tab-duplicate-resolution"
            onClick={() => setActiveTab('duplicates')}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'duplicates' ? 700 : 400,
              borderBottom: activeTab === 'duplicates' ? '3px solid #e53935' : '3px solid transparent',
              color: activeTab === 'duplicates' ? '#e53935' : '#616161',
              fontSize: '14px',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Duplicate Resolution
          </button>
        </div>

        {/* Duplicate Resolution Tab Content */}
        {activeTab === 'duplicates' && <DuplicateResolutionTab />}

        {/* All Candidates Tab Content */}
        {activeTab === 'candidates' && (<>
        <div className="filter-bar-card" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start' }}>
          <div className="search-wrapper" style={{ flex: 1, minWidth: '200px', maxWidth: '300px' }}>
            <SearchIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search by Candidate Name"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setFilterName("");
                setFilterEmail("");
                setPage(1);
              }}
            />
          </div>
          <ResumeScoreFilters
            selectionStatus={scoreFilters.selectionStatus}
            minScore={scoreFilters.minScore}
            sortOrder={scoreFilters.sortOrder}
            onChange={handleScoreFilterChange}
          />

          {/* Reset Filters / Clean search state button if any filter is active */}
          {searchText && (
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setSearchText("");
                setFilterName("");
                setFilterEmail("");
                setPage(1);
              }}
              sx={{ color: '#2777AA', textTransform: 'none', fontWeight: 600 }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Directory Content */}
        <div className="candidate-rows-container">
          {showLoader ? (
            <div style={{ display: "flex", height: 400, justifyContent: "center", alignItems: "center", width: "100%" }}>
              <ScaleLoader size={250} color={"#0576DC"} loading={showLoader} />
            </div>
          ) : candidateData?.length === 0 ? (
            <div style={{ height: 300, width: "100%", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
              <img src={noTableData} width={60} alt="No Data" />
              <div style={{ marginTop: 5, fontWeight: "500", fontSize: 25, fontFamily: "'Noto Sans', sans-serif" }}>
                There are no recent Candidate to show.
              </div>
            </div>
          ) : (
            <>
              {/* Column Headers (Desktop) */}
              <div className="directory-table-header">
                <span>CANDIDATE INFO</span>
                <span className="header-client">CLIENT</span>
                <span className="header-source">SOURCE</span>
                <span>ACTIVE APPLICATIONS (JOB, STATUS)</span>
                <span className="header-score">RESUME SCORE</span>
                <span>LAST UPDATED</span>
                <span style={{ textAlign: 'right' }}>ACTIONS</span>
              </div>

              {candidateData.map((val) => (
                <div className="candidate-row-card" key={val.profile_id}>
                  <div className="info-column">
                    <div className="name">{val.candidate_name}</div>
                    <div className="email">{val.email}</div>
                    {/* Tablet source inline */}
                    <div className="source-inline" style={{ display: 'none' }}>
                      <span className="source-badge">
                        {val.supplierInfo?.supplier_org_name || (val.creator_name ? `Direct — Added by ${val.creator_name}` : "Direct")}
                      </span>
                    </div>
                    {(() => {
                      const isNonResume = val.pipeline_type === 'non_resume_based' || val.JobPost?.pipeline_type === 'non_resume_based' || (!val.url && !val.profile_path && !val.resume_url);
                      return isNonResume ? (
                        <span
                          className="resume-link"
                          style={{ color: '#aaa', cursor: 'not-allowed', pointerEvents: 'none', textDecoration: 'none' }}
                        >
                          <DescriptionIcon style={{ fontSize: '0.9rem' }} />
                          View Resume
                        </span>
                      ) : (
                        <a
                          href="#!"
                          className="resume-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handleViewResume(val);
                          }}
                        >
                          <DescriptionIcon style={{ fontSize: '0.9rem' }} />
                          View Resume
                        </a>
                      );
                    })()}
                  </div>

                  <div className="client-column">
                    <span className="client-badge">
                      {val.client_name || "—"}
                    </span>
                  </div>

                  <div className="source-column">
                    <span className="source-badge">
                      {val.supplierInfo?.supplier_org_name || (val.creator_name ? `Direct — Added by ${val.creator_name}` : "Direct")}
                    </span>
                  </div>

                  <div className="applications-column">
                    <div className="application-chip">
                      <div className="job-info">
                        <span className="job-code">{val.job_code}</span>
                        <span className="job-title">{val.JobPost?.title || "Position"}</span>
                      </div>
                      <span className={`status-badge ${getStatusColorClass(val.detailed_status)}`}>
                        {parseStatusLabel(val.detailed_status)}
                      </span>
                    </div>
                  </div>

                  <div className="score-column">
                    <ResumeScoreChip
                      score={val.resume_score}
                      status={val.resume_selection_status}
                      reason={val.resume_selection_reason}
                      explanationHtml={val.resume_explanation_html}
                      pending={isScorePending(val)}
                      notApplicable={isNonResumeProfile(val)}
                    />
                  </div>

                  <div className="last-updated-column">
                    {val.status_date ? format(new Date(val.status_date), "dd MMM yyyy") : "—"}
                  </div>

                  <div className="actions-column">
                    <ProfileMenu
                      details={val}
                      navigate={navigate}
                      location={location}
                      setAlert={setAlert}
                      setAlertContent={setAlertContent}
                      setAlertType={setAlertType}
                    />
                  </div>
                </div>
              ))}

              {/* Pagination */}
              <div className="redesigned-pagination">
                <Pagination
                  count={Math.ceil(listCount / 10)}
                  page={page}
                  onChange={handlePageChange}
                  showFirstButton
                  showLastButton
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                />
              </div>
            </>
          )}
        </div>

        </>)}

        <Notification
          message={alertContent}
          open={alert}
          severity={alertType}
          onClose={handleAlert}
        />
      </div>
    );
  }

  return (
    <div style={{ marginTop: "20px" }}>
      <Grid
        container
        xs={12}
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        {!isAdmin && (
          <Button
            variant="contained"
            aria-haspopup="true"
            onClick={handleNavigate}
            style={{ background: "#2777AA", width: "214px" }}
          >
            + Add Candidate
          </Button>
        )}
      </Grid>
      <Grid container xs={12} style={{ marginTop: "2%" }}>
        <Grid
          item
          xs={6}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <span className={classes.headingText}>Candidate</span>
          {jobCode && (
            <Chip
              label={jobTitle ? `Job: ${jobCode} - ${jobTitle}` : `Job: ${jobCode}`}
              onDelete={() => {
                setJobCode("");
                setJobTitle("");
              }}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ ml: 2, fontWeight: 'bold' }}
            />
          )}
        </Grid>
        <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
          <ResumeScoreFilters
            selectionStatus={scoreFilters.selectionStatus}
            minScore={scoreFilters.minScore}
            sortOrder={scoreFilters.sortOrder}
            onChange={handleScoreFilterChange}
          />
          <TextField
            name="search"
            label="Search by Candidate Name"
            variant="outlined"
            size="small"
            style={{ backgroundColor: '#fff', width: '230px' }}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setFilterName("");
              setFilterEmail("");
              setPage(1);
            }}
            InputProps={{
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchText('')}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            autoComplete="off"
          />
          <TextField
            name="jobSearch"
            label="Search by Job Code"
            variant="outlined"
            size="small"
            style={{ backgroundColor: '#fff', width: '200px' }}
            value={jobCode}
            onChange={(e) => {
              setJobCode(e.target.value);
              setPage(1);
              if (jobTitle) setJobTitle(""); // Clear title if searching manually
            }}
            InputProps={{
              endAdornment: jobCode ? (
                <InputAdornment position="end">
                  <IconButton onClick={() => {
                    setJobCode('');
                    setJobTitle('');
                  }}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            autoComplete="off"
          />
          <FormControl variant="outlined" size="small" style={{ width: '220px', backgroundColor: '#fff' }}>
            <InputLabel>Profile Status</InputLabel>
            <Select
              value={profileStatus}
              label="Profile Status"
              onChange={(e) => {
                setProfileStatus(e.target.value);
                setPage(1);
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

      </Grid>
      <Grid container xs={12} className={classes.tableGrid}>
        {showLoader ? (
          <div
            style={{
              display: "flex",
              height: 400,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <ScaleLoader size={250} color={"#0576DC"} loading={showLoader} />
          </div>
        ) : candidateData?.length === 0 ? (
          <div
            style={{
              height: 300,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <img src={noTableData} width={60} alt="No Data" />
            <div
              style={{
                marginTop: 5,
                fontWeight: "500",
                fontSize: 25,
                fontFamily: "'Noto Sans', sans-serif",
              }}
            >
              There are no recent Candidate to show.
            </div>
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: 650 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell className={classes.tableHeadCell}>Name</TableCell>
                  {isAdmin && (
                    <TableCell className={classes.tableHeadCell}>Client Name</TableCell>
                  )}
                  {isAdmin && (
                    <TableCell className={classes.tableHeadCell}>
                      Supplier
                    </TableCell>
                  )}
                  <TableCell className={classes.tableHeadCell}>
                    Job Code
                  </TableCell>
                  {!isAdmin && (
                    <TableCell className={classes.tableHeadCell}>
                      Job Status
                    </TableCell>
                  )}
                  {!isAdmin && (
                    <>
                      <TableCell className={classes.tableHeadCell}>
                        Current Address
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        Budget
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>
                        Budget UOM
                      </TableCell>
                    </>
                  )}
                  {/* <TableCell className={classes.tableHeadCell}>
                    Feedback
                  </TableCell> */}
                  <TableCell className={classes.tableHeadCell}>
                    Resume Score
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Status
                  </TableCell>
                  {!isAdmin && (
                    <TableCell className={classes.tableHeadCell}>
                      Status Update Date
                    </TableCell>
                  )}
                  <TableCell className={classes.tableHeadCell}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <>
                <TableBody className={classes.noHorizontalBorderTableRow}>
                  {candidateData.map((val) => {
                    return (
                      <TableRow key={val.profile_id}>
                        <TableCell className={classes.tableBodyCell}>
                          {val.candidate_name}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className={classes.tableBodyCell}>
                            {val.client_name || "—"}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className={classes.tableBodyCell}>
                            {val.supplierInfo?.supplier_org_name || (val.creator_name ? `Direct — Added by ${val.creator_name}` : "Direct")}
                          </TableCell>
                        )}
                        <TableCell className={classes.tableBodyCell}>
                          {val.job_code}
                        </TableCell>
                        {!isAdmin && (
                          <TableCell className={classes.tableBodyCell}>
                            {parseStatusLabel(val.JobPost?.job_status) || '-'}
                          </TableCell>
                        )}
                        {!isAdmin && (
                          <>
                            <TableCell className={classes.tableBodyCell}>
                              {val.candidate_current_location}
                            </TableCell>
                            <TableCell>{val.budget}</TableCell>
                            <TableCell>{val.budget_eom}</TableCell>
                          </>
                        )}
                        <TableCell className={classes.tableBodyCell}>
                          <ResumeScoreChip
                            score={val.resume_score}
                            status={val.resume_selection_status}
                            reason={val.resume_selection_reason}
                            explanationHtml={val.resume_explanation_html}
                            pending={isScorePending(val)}
                            notApplicable={isNonResumeProfile(val)}
                          />
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          <Tooltip
                            title={parseStatusLabel(val?.detailed_status) || 'No detailed status'}
                            arrow
                            placement="top"
                          >
                            <span> {/* Tooltip requires a single child with onMouseOver support */}
                              <Chip
                                label={parseStatusLabel(val?.detailed_status)}
                                size="small"
                                color="default"
                                sx={{ cursor: 'help' }} // Visual hover indicator
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        {!isAdmin && (
                          <TableCell className={classes.tableBodyCell}>
                            {val.status_date ? format(new Date(val.status_date), "dd-MM-yyyy") : "-"}
                          </TableCell>
                        )}
                        <TableCell>
                          <MoreMenu
                            details={val}
                            setAlertContent={setAlertContent}
                            setAlertType={setAlertType}
                            setAlert={setAlert}
                            navigate={navigate}
                            location={location}
                            isAdmin={isAdmin}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </>
            </Table>
            <Stack
              spacing={2}
              sx={{
                padding: 2,
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <Pagination
                classes={{
                  root: classes.pagination,
                }}
                count={Math.ceil(listCount / 10)}
                page={page}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
              />
            </Stack>
          </TableContainer>
        )}
      </Grid>
      <Notification
        message={alertContent}
        open={alert}
        severity={alertType}
        onClose={handleAlert}
      />
    </div>
  );
};

export default CandidateMangComp;


function MoreMenu({
  details,
  setAlertContent,
  setAlertType,
  setAlert,
  navigate,
  location,
  isAdmin,
}) {
  const [delOpen, setDelOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // React Query Mutation
  const { mutate: deleteCandidate } = useDeleteCandidate();

  const refDel = useRef(delOpen);

  const handleDialogInfo = () => {
    setAnchorEl(null);
  };

  const handleDialogdel = () => {
    setDelOpen(!delOpen);
  };
  const open = Boolean(anchorEl);

  const handleSupplierDelete = () => {
    deleteCandidate(details.profile_id, {
      onSuccess: (data) => {
        handleDialogdel();
        setAlertContent(data?.message || "Candidate deleted successfully");
        setAlertType("success");
        setAlert(true);
      },
      onError: (error) => {
        setAlertContent(error?.response?.data?.message || "Error deleting candidate");
        setAlertType("error");
        setAlert(true);
      }
    });
  };

  // ✅ SAME CONDITIONAL LOGIC FROM AdminStatusUpdateForm
  const getFeedbackFieldsForStatus = (status) => {
    const profileOnlyStages = [
      "Vendor_Submission",
      "Internal_Evaluation",
      "Internal_Screening",
      "Internal_Feedback",
      "Submitted_To_Client",
      "Client_Evaluation"
    ];

    const interviewOnlyStages = [
      "L1_Round",
      "L2_Round",
      "L3_Round"
    ];

    const bothFeedbackStages = [
      "Rejected",
      "Interview_Cancelled",
      "Position_on_hold",
      "Close-No_Client_Response"
    ];

    if (profileOnlyStages.includes(status)) {
      return 'profile';
    } else if (interviewOnlyStages.includes(status)) {
      return 'interview';
    } else if (bothFeedbackStages.includes(status)) {
      return 'both';
    }

    return null;
  };



  // ✅ Calculate feedback type for conditional display
  const feedbackType = getFeedbackFieldsForStatus(details?.detailed_status);

  return (
    <>
      <IconButton
        onClick={() => {
          navigate("/candidate_data/Viewcandidate", {
            state: { CandidateDetails: details, from: location.search },
          });
        }}
      >
        <Visibility sx={{ color: "#2777AA" }} />
      </IconButton>
      <IconButton
        onClick={() => {
          navigate("/candidate_data/editcandidate", {
            state: { CandidateDetails: details, from: location.search },
          });
        }}
      >
        <EditIcon sx={{ color: "#2777AA" }} />
      </IconButton>
      <Tooltip title="Assign to Job" arrow placement="top">
        <IconButton
          onClick={() => {
            navigate("/candidate_data/addCandidate", {
              state: {
                prefillCandidates: [{
                  candidate_name: details.candidate_name,
                  candidate_current_location: details.candidate_current_location,
                  mobile: details.mobile,
                  email: details.email,
                  currency: details.currency,
                  budget: details.budget,
                  budget_eom: details.budget_eom,
                  gender: details.gender || details.CandidateDetails?.gender || "",
                  notice_period: (details.notice_period !== undefined && details.notice_period !== null && details.notice_period !== "")
                    ? details.notice_period
                    : (details.timeline || details.CandidateDetails?.notice_period || ""),
                  coreSkillId: details.coreSkillId || details.profileCoreSkill?.id,
                  techSkills: details.techSkills || (details.profileCoreSkill ? [details.profileCoreSkill.name] : []),
                  profile_path: details.profile_path || details.resume_url || details.url || "Existing Resume",
                  s3Key_profile_path: details.s3Key_profile_path || details.profile_path || details.resume_url || details.url || "Existing Resume",
                  important_note: details.important_note || "",
                  aadhar_doc: details.aadhar_doc || "",
                  s3Key_aadhar_doc: details.s3Key_aadhar_doc || "",
                  pan_doc: details.pan_doc || "",
                  s3Key_pan_doc: details.s3Key_pan_doc || ""
                }],
                selectedSupplierId: details.supplier_id || details.supplierInfo?.supplier_id,
                from: location.pathname + location.search
              }
            });
          }}
        >
          <PersonAddIcon sx={{ color: "#2777AA" }} />
        </IconButton>
      </Tooltip>
      <IconButton
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
      >
        <InfoIcon sx={{ color: "#2777AA" }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleDialogInfo}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <div
            style={{
              display: "block",
              alignItems: "center",
              justifyContent: "center",
              padding: "1em",
              gap: "10px",
            }}
          >
            <p>
              <b>Date</b>: {details.updatedAt ? format(new Date(details.updatedAt), "yyyy-MM-dd") : ""}
            </p>
            <p>
              <b>Status</b>: {parseStatusLabel(details?.detailed_status) || "Update Soon"}
            </p>

            {details.status_group === 'Interview_Scheduled' && (
              <>
                <p>
                  <b>Interview Scheduled Date</b>:{" "}
                  {details.interview_datetime || "Update Soon"}
                </p>
                <p>
                  <b>Interview Link</b>:{" "}
                  {details.interview_link ? (
                    <a
                      href={details.interview_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#2777AA",
                        textDecoration: "underline",
                        wordBreak: "break-all"
                      }}
                    >
                      {details.interview_link}
                    </a>
                  ) : (
                    "Update Soon"
                  )}
                </p>
              </>
            )}

            {/* ✅ CONDITIONAL FEEDBACK DISPLAY - SAME LOGIC */}
            {feedbackType === 'profile' || feedbackType === 'both' ? (
              <p>
                <b>Profile Feedback</b>:{" "}
                {details.profile_feedback || "Update Soon"}
              </p>
            ) : null}

            {feedbackType === 'interview' || feedbackType === 'both' ? (
              <p>
                <b>Interview Feedback</b>:{" "}
                {details.interview_feedback || "Update Soon"}
              </p>
            ) : null}

          </div>
        </Grid>
      </Popover>

      <CustomModel
        handleDialog={handleDialogdel}
        open={delOpen}
        title="Delete Job"
      >
        <div
          style={{
            textAlign: "center",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: "24px",
          }}
        >
          Do you want to delete the Candidate Profile?
        </div>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1em",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => handleSupplierDelete(details?.job_id)}
              style={{ background: "#2777AA" }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              onClick={() => handleDialogdel()}
              style={{ background: "#2777AA" }}
            >
              NO
            </Button>
          </div>
        </Grid>
      </CustomModel>

      {isAdmin && (
        <IconButton
          ref={refDel}
          onClick={() => {
            setDelOpen(true);
          }}
        >
          <DeleteIcon sx={{ color: "red" }} />
        </IconButton>
      )}
    </>
  );
}

function ProfileMenu({
  details,
  navigate,
  location,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewProfile = () => {
    handleClose();
    navigate("/candidate_data/Viewcandidate", {
      state: { CandidateDetails: details, from: location.pathname + location.search },
    });
  };

  const handleEditProfile = () => {
    handleClose();
    navigate("/candidate_data/editcandidate", {
      state: { CandidateDetails: details, from: location.pathname + location.search },
    });
  };

  const handleAssignToJob = () => {
    handleClose();
    navigate("/candidate_data/addCandidate", {
      state: {
        prefillCandidates: [{
          candidate_name: details.candidate_name,
          candidate_current_location: details.candidate_current_location,
          mobile: details.mobile,
          email: details.email,
          currency: details.currency,
          budget: details.budget,
          budget_eom: details.budget_eom,
          gender: details.gender || details.CandidateDetails?.gender || "",
          notice_period: (details.notice_period !== undefined && details.notice_period !== null && details.notice_period !== "")
            ? details.notice_period
            : (details.timeline || details.CandidateDetails?.notice_period || ""),
          coreSkillId: details.coreSkillId || details.profileCoreSkill?.id,
          techSkills: details.techSkills || (details.profileCoreSkill ? [details.profileCoreSkill.name] : []),
          profile_path: (details.pipeline_type === 'non_resume_based' || (!details.profile_path && !details.resume_url && !details.url))
            ? ""
            : (details.profile_path || details.resume_url || details.url || "Existing Resume"),
          s3Key_profile_path: (details.pipeline_type === 'non_resume_based' || (!details.profile_path && !details.resume_url && !details.url))
            ? ""
            : (details.s3Key_profile_path || details.profile_path || details.resume_url || details.url || "Existing Resume"),
          important_note: details.important_note || "",
          aadhar_doc: details.aadhar_doc || "",
          s3Key_aadhar_doc: details.s3Key_aadhar_doc || "",
          pan_doc: details.pan_doc || "",
          s3Key_pan_doc: details.s3Key_pan_doc || ""
        }],
        selectedSupplierId: details.supplier_id || details.supplierInfo?.supplier_id,
        from: location.pathname + location.search
      }
    });
  };

  const handleInfo = () => {
    handleClose();
    setDetailsOpen(true);
  };



  return (
    <div>
      <button className="profile-btn" onClick={handleClick}>
        Profile <ExpandMoreIcon fontSize="small" />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: '12px',
            minWidth: '150px'
          }
        }}
      >
        <MenuItem onClick={handleViewProfile} sx={{ fontSize: '0.875rem', py: 1 }}>
          View Profile
        </MenuItem>
        <MenuItem onClick={handleEditProfile} sx={{ fontSize: '0.875rem', py: 1 }}>
          Edit Profile
        </MenuItem>
        <MenuItem onClick={handleAssignToJob} sx={{ fontSize: '0.875rem', py: 1 }}>
          Assign to Job
        </MenuItem>
        <MenuItem onClick={handleInfo} sx={{ fontSize: '0.875rem', py: 1 }}>
          View Details
        </MenuItem>
      </Menu>

      <Dialog
        open={detailsOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setDetailsOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Candidate Details</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '10px' }}>
            <div style={{ margin: 0 }}><strong>Candidate Name:</strong> {details.candidate_name}</div>
            <div style={{ margin: 0 }}><strong>Email:</strong> {details.email}</div>
            <div style={{ margin: 0 }}><strong>Job Code:</strong> {details.job_code}</div>
            <div style={{ margin: 0 }}><strong>Status:</strong> {parseStatusLabel(details?.detailed_status) || "Update Soon"}</div>
            <div style={{ margin: 0 }}><strong>Last Updated:</strong> {details.updatedAt ? format(new Date(details.updatedAt), "yyyy-MM-dd") : ""}</div>

            {details.status_group === 'Interview_Scheduled' && (
              <>
                <div style={{ margin: 0 }}><strong>Interview Date:</strong> {details.interview_datetime || "Update Soon"}</div>
                <div style={{ margin: 0 }}><strong>Link:</strong> {details.interview_link || "Update Soon"}</div>
              </>
            )}

            <div style={{ background: '#F9FAFB', padding: '12px', borderRadius: '8px' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem', color: '#6B7280' }}>Interview Feedback</p>
              <p style={{ margin: 0 }}>{details.interview_feedback || "No feedback yet."}</p>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDetailsOpen(false)} variant="contained" sx={{ borderRadius: '8px', background: '#2563EB' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function FilterMenu({
  filterName,
  setFilterName,
  filterEmail,
  setFilterEmail,
  jobCode,
  setJobCode,
  setSearchText,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <button className="filters-btn" onClick={handleClick}>
        <FilterListIcon fontSize="small" />
        Filters
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            p: 3,
            mt: 1,
            width: 320,
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <Stack spacing={2.5}>
          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>Advanced Filters</div>
          <TextField
            label="Candidate Name"
            size="small"
            fullWidth
            value={filterName}
            onChange={(e) => {
              setFilterName(e.target.value);
              if (setSearchText) setSearchText("");
            }}
          />
          <TextField
            label="Candidate Email"
            size="small"
            fullWidth
            value={filterEmail}
            onChange={(e) => {
              setFilterEmail(e.target.value);
              if (setSearchText) setSearchText("");
            }}
          />
          <TextField
            label="Job Code"
            size="small"
            fullWidth
            value={jobCode}
            onChange={(e) => setJobCode(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleClose}
            sx={{ mt: 1, borderRadius: '8px', background: '#2563EB', textTransform: 'none' }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Popover>
    </div>
  );
}

